import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { CreateVisibility } from "@/components/client/CreateVisibility";
import { api, errMsg } from "@/lib/api";

const EMPTY = {
  ragione_sociale: "", referente: "", email: "", telefono: "", piva: "", indirizzo: "", sito_web: "",
  instagram: "", facebook: "", linkedin: "", tiktok: "", youtube: "", settore: "", socio_id: "none", note: "", stato: "attivo",
  visibilita: "tutti", membri: [],
  codice_fiscale: "", rea: "", pec: "", sdi: "", legale_rappresentante: "", whatsapp: "", iban: "", banca: "", sedi: [], siti: [], insegne: [],
};
const FISCAL = [["codice_fiscale", "Codice fiscale"], ["rea", "REA"], ["pec", "PEC"], ["sdi", "Codice SDI (codice univoco)"],
  ["legale_rappresentante", "Legale rappresentante"], ["whatsapp", "WhatsApp"], ["iban", "IBAN"], ["banca", "Banca"]];
const splitTags = (s) => (s || "").split(/[;,\n]/).map((t) => t.trim()).filter(Boolean);

const ListEditor = ({ title, items, field, placeholder, onChange, testid }) => (
  <div className="col-span-2 space-y-2" data-testid={testid}>
    <div className="flex items-center justify-between"><Label className="text-xs font-bold text-slate-600">{title}</Label>
      <Button type="button" variant="outline" size="sm" className="rounded-full font-bold" onClick={() => onChange([...items, { etichetta: "", [field]: "" }])} data-testid={`${testid}-add`}><Plus className="h-4 w-4 mr-1" />Aggiungi</Button></div>
    {items.map((it, i) => (
      <div key={i} className="flex gap-2">
        <Input value={it.etichetta} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, etichetta: e.target.value } : x)))} placeholder="Etichetta" className="rounded-xl w-1/3" data-testid={`${testid}-label-${i}`} />
        <Input value={it[field]} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [field]: e.target.value } : x)))} placeholder={placeholder} className="rounded-xl flex-1" data-testid={`${testid}-value-${i}`} />
        <Button type="button" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Rimuovi" data-testid={`${testid}-remove-${i}`}><X className="h-4 w-4" /></Button>
      </div>
    ))}
  </div>
);

const MAIN = [
  ["ragione_sociale", "Ragione sociale *"], ["referente", "Referente"], ["email", "Email"], ["telefono", "Telefono"],
  ["piva", "P.IVA"], ["settore", "Settore"],
];
const WEB = [["indirizzo", "Indirizzo (sede principale)"], ["sito_web", "Sito web principale"]];
const SOCIAL = [["instagram", "Instagram"], ["facebook", "Facebook"], ["linkedin", "LinkedIn"], ["tiktok", "TikTok"], ["youtube", "YouTube"]];

const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#E8173A] mb-3">{title}</div>
    <div className="grid grid-cols-2 gap-4">{children}</div>
  </div>
);

export const ClientFormDialog = ({ open, onOpenChange, client, initial, users = [], settori = [], onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const base = client ? { ...EMPTY, ...client, socio_id: client.socio_id || "none" } : { ...EMPTY, ...initial };
    if (open) setF({ ...base, insegne_txt: (base.insegne || []).join("; ") });
  }, [open, client]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const input = ([k, label]) => (
    <div key={k} className="space-y-1.5">
      <Label className="text-xs font-bold text-slate-600">{label}</Label>
      <Input value={f[k]} onChange={set(k)} className="rounded-xl" list={k === "settore" ? "settori-list" : undefined}
        placeholder={SOCIAL.find((s) => s[0] === k) ? "https://..." : ""} data-testid={`client-form-${k}`} />
    </div>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!f.ragione_sociale.trim()) return toast.error("La ragione sociale è obbligatoria");
    setSaving(true);
    const body = { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? ""])), socio_id: f.socio_id === "none" ? null : f.socio_id,
      insegne: splitTags(f.insegne_txt), visibilita: client ? undefined : f.visibilita, membri: client ? undefined : f.membri, sedi: (f.sedi || []).filter((s) => s.indirizzo.trim() || s.etichetta.trim()), siti: (f.siti || []).filter((s) => s.url.trim()) };
    try {
      const r = client ? await api.put(`/clients/${client.id}`, body) : await api.post("/clients", body);
      toast.success(client ? "Cliente aggiornato" : "Cliente creato");
      onSaved?.(r.data); window.dispatchEvent(new Event("core-clients-changed"));
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl core-scroll" data-testid="client-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-black text-[#0A1128]">{client ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-7">
          <datalist id="settori-list">{settori.map((s) => <option key={s} value={s} />)}</datalist>
          <Section title="Dati principali">
            {MAIN.map(input)}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-600">Socio responsabile</Label>
              <Select value={f.socio_id} onValueChange={set("socio_id")}>
                <SelectTrigger className="rounded-xl" data-testid="client-form-socio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {users.filter((u) => u.is_active || u.id === f.socio_id).map((u) => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-600">Stato</Label>
              <Select value={f.stato} onValueChange={set("stato")}>
                <SelectTrigger className="rounded-xl" data-testid="client-form-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="archiviato">Archiviato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Section>
          {!client && <CreateVisibility value={{ visibilita: f.visibilita, membri: f.membri }} onChange={(v) => setF((p) => ({ ...p, ...v }))} users={users} testid="client-form-visibility" />}
          <Section title="Dati fiscali e amministrativi">{FISCAL.map(input)}</Section>
          <Section title="Sedi e web">
            {WEB.map(input)}
            <ListEditor title="Altre sedi" items={f.sedi || []} field="indirizzo" placeholder="Via, città" onChange={set("sedi")} testid="client-form-sedi" />
            <ListEditor title="Altri siti web" items={f.siti || []} field="url" placeholder="https://…" onChange={set("siti")} testid="client-form-siti" />
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-bold text-slate-600">Insegne / brand (separate da ;)</Label>
              <Input value={f.insegne_txt || ""} onChange={set("insegne_txt")} className="rounded-xl" placeholder="Insegna 1; Insegna 2" data-testid="client-form-insegne" />
            </div>
          </Section>
          <Section title="Social">{SOCIAL.map(input)}</Section>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-600">Note</Label>
            <Textarea rows={3} value={f.note} onChange={set("note")} className="rounded-xl" data-testid="client-form-note" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="client-form-cancel">Annulla</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] px-6" data-testid="client-form-submit">
              {saving ? "Salvataggio..." : client ? "Salva modifiche" : "Crea cliente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
